<script setup lang="ts">
import { computed, markRaw, ref } from 'vue'

import modal from '@/services/modal'

import IconActionConfig from '@/components/atoms/icons/IconActionConfig.vue'
import IconActionDelete from '@/components/atoms/icons/IconActionDelete.vue'
import IconActionDuplicate from '@/components/atoms/icons/IconActionDuplicate.vue'
import IconActionMove from '@/components/atoms/icons/IconActionMove.vue'
import IconActionToDown from '@/components/atoms/icons/IconActionToDown.vue'
import IconActionToUp from '@/components/atoms/icons/IconActionToUp.vue'
import IconGridLarge from '@/components/atoms/icons/IconGridLarge.vue'
import IconGridMedium from '@/components/atoms/icons/IconGridMedium.vue'
import IconGridSmall from '@/components/atoms/icons/IconGridSmall.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'

const props = defineProps<{
    element: any
    schema: any
    hasMove?: boolean
    hasResize?: boolean
    hasConfig?: boolean
    hasClone?: boolean
    hasDelete?: boolean
}>()

const betaStore = useEditorBetaStore()

const grids = ref([
    { name: 'grande', value: 12, icon: markRaw(IconGridLarge) },
    { name: 'média', value: 6, icon: markRaw(IconGridMedium) },
    { name: 'pequena', value: 4, icon: markRaw(IconGridSmall) },
])

const currentGridIndex = computed(() => grids.value.findIndex((grid: any) => grid.value === props.element.grid))
const dynamicIconGrid = computed(() => {
    const index = currentGridIndex.value < 0 ? 0 : currentGridIndex.value
    const icon = index === grids.value.length - 1
        ? grids.value[0].icon
        : grids.value[index + 1].icon

    return icon
})

const onResizeElement = () => {
    const index = currentGridIndex.value < 0 ? 0 : currentGridIndex.value
    const newGrid = index === grids.value.length - 1
        ? grids.value[0].value
        : grids.value[index + 1].value

    betaStore.updateElementBySchema(props.schema, {
        ...props.element,
        grid: newGrid,
    })
}

const onConfigElement = () => {
    betaStore.setSelectedElementBySchema(props.schema)
}

const onCloneElement = async () => {
    const result = await modal.confirmation('Confirmar clonagem', 'Deseja realmente clonar este elemento?', {
        type: 'info',
        textConfirm: 'Sim, clonar',
        textCancel: 'Não, voltar',
    })

    if (!result) return

    betaStore.cloneElementBySchema(props.schema)
}

const onDeleteElement = async () => {
    const result = await modal.confirmation('Confirmar Exclusão', 'Deseja realmente excluir este elemento?', {
        type: 'danger',
        textConfirm: 'Sim, excluir',
        textCancel: 'Não, voltar',
    })

    if (!result) return

    betaStore.removeElementBySchema(props.schema)
}
</script>

<template>
    <div class="beta-element-actions actions-element beta-centered beta-compact">
        <button v-if="props.hasMove !== false" class="beta-action-move-element action-move-element cursor-grab">
            <span class="sr-only">Mover elemento</span>
            <icon-action-move class="h-[18px] w-[18px]" />
        </button>

        <button
            type="button"
            class="action-change-position-up"
            :disabled="betaStore.isFirstElementBySchema(props.schema)"
            @click="betaStore.moveElementBySchema(props.schema, 'up')"
        >
            <span class="sr-only">Mover para cima</span>
            <icon-action-to-up class="h-[18px] w-[18px]" />
        </button>

        <button
            type="button"
            class="action-change-position-down"
            :disabled="betaStore.isLastElementBySchema(props.schema)"
            @click="betaStore.moveElementBySchema(props.schema, 'down')"
        >
            <span class="sr-only">Mover para baixo</span>
            <icon-action-to-down class="h-[18px] w-[18px]" />
        </button>

        <button v-if="props.hasResize !== false" type="button" class="action-resize-element" @click="onResizeElement">
            <span class="sr-only">Redimensionar</span>
            <component class="h-[18px] w-[18px]" :is="dynamicIconGrid" />
        </button>

        <button v-if="props.hasConfig === true" type="button" class="action-config-element" @click="onConfigElement">
            <span class="sr-only">Configurar</span>
            <icon-action-config class="h-[20px] w-[20px]" />
        </button>

        <button v-if="props.hasClone !== false" type="button" class="action-duplicate-element" @click="onCloneElement">
            <span class="sr-only">Duplicar</span>
            <icon-action-duplicate class="h-[18px] w-[18px]" />
        </button>

        <button v-if="props.hasDelete !== false" type="button" class="action-delete-element is-danger" @click="onDeleteElement">
            <span class="sr-only">Excluir</span>
            <icon-action-delete class="h-[18px] w-[18px]" />
        </button>
    </div>
</template>

<style scoped>
.actions-element {
    @apply absolute -top-[18px] right-5 z-[80] flex items-center justify-between gap-1.5 rounded-full border border-[#D7D7D7] bg-[rgba(245,245,245,0.96)] px-2.5 py-1.5 text-[#060606] opacity-100 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur-sm;
    pointer-events: auto;
}

.actions-element button {
    @apply flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200 hover:bg-white/80 disabled:cursor-not-allowed;
}

.actions-element .action-move-element :deep(svg),
.actions-element .action-change-position-up :deep(svg),
.actions-element .action-change-position-down :deep(svg),
.actions-element .action-config-element :deep(svg),
.actions-element .action-duplicate-element :deep(svg),
.actions-element .action-delete-element :deep(svg),
.actions-element .action-resize-element :deep(svg) {
    @apply opacity-55 transition-opacity duration-200 hover:opacity-100;
}

.actions-element .action-move-element :deep(svg),
.actions-element .action-change-position-up :deep(svg),
.actions-element .action-change-position-down :deep(svg),
.actions-element .action-config-element :deep(svg),
.actions-element .action-duplicate-element :deep(svg),
.actions-element .action-delete-element :deep(svg) {
    @apply stroke-[#060606];
}

.actions-element .action-change-position-up:disabled :deep(svg),
.actions-element .action-change-position-down:disabled :deep(svg) {
    @apply stroke-[#060606] opacity-30;
}

.actions-element .action-resize-element :deep(svg) {
    @apply fill-[#060606];
}

.actions-element.beta-centered {
    @apply right-auto left-1/2 -translate-x-1/2;
}

.actions-element.beta-compact {
    @apply gap-1 px-2 py-1;
}

.actions-element.beta-compact button {
    @apply h-7 w-7;
}

.actions-element.beta-compact :deep(svg) {
    transform: scale(0.88);
    transform-origin: center;
}

.actions-element button.is-danger {
    @apply text-[#D0657B];
}
</style>
